import { useEffect, useState } from 'react';

import { Close as CloseIcon } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
} from '@mui/material';

import type { Transformer, TransformerVersion } from '@/types';
import { logger } from '@/utils/logger';

import {
  useGetTransformerEmailsQuery,
  useGetTransformerVersionsQuery,
  useUpdateTransformerEmailsMutation,
  useUpdateTransformerInfoMutation,
} from '../api/transformersApi';
import {
  formatMaxDataProcTime,
  getHoursOptions,
  getMinutesOptions,
  parseMaxDataProcTime,
} from '../utils/maxDataProcTime';

interface PropertiesModalProps {
  open: boolean;
  recipe: Transformer | null;
  appId: string;
  onClose: () => void;
}

export const PropertiesModal = ({ open, recipe, appId, onClose }: PropertiesModalProps) => {
  // Form state
  const [tag, setTag] = useState('');
  const [description, setDescription] = useState('');
  const [thresholdHours, setThresholdHours] = useState('00');
  const [thresholdMinutes, setThresholdMinutes] = useState('00');
  const [emailInput, setEmailInput] = useState('');
  const [emails, setEmails] = useState<string[]>([]);
  const [emailValidation, setEmailValidation] = useState('');
  const [processingType, setProcessingType] = useState<'batch' | 'stateless'>('batch');
  const [saveError, setSaveError] = useState('');
  const [showSaved, setShowSaved] = useState(false);

  // API queries
  const { data: versions } = useGetTransformerVersionsQuery(
    {
      transBaseId: recipe?.baseId || '',
      appId,
    },
    {
      skip: !recipe?.baseId || !open,
    }
  );

  const { data: emailsData } = useGetTransformerEmailsQuery(recipe?.baseId || '', {
    skip: !recipe?.baseId || !open,
  });

  // Mutations
  const [updateTransformerInfo, { isLoading: isSaving }] = useUpdateTransformerInfoMutation();
  const [updateEmails] = useUpdateTransformerEmailsMutation();

  // Versions are sorted descending (latest first), so [0] is the current/latest version
  const currentVersion: TransformerVersion | undefined = versions?.[0];

  // Initialize form from the latest version data (matching legacy filesBar.js behavior)
  // Legacy reads Description, Environ, ProcessingType, maxDataProcTime from versions[0]
  useEffect(() => {
    if (open && currentVersion) {
      setTag(currentVersion.environ || '');
      setDescription(currentVersion.description || '');
      setProcessingType((currentVersion.processingType as 'batch' | 'stateless') || 'batch');

      const { hours, minutes } = parseMaxDataProcTime(currentVersion.maxDataProcTime);
      setThresholdHours(hours);
      setThresholdMinutes(minutes);

      setSaveError('');
      setShowSaved(false);
    }
  }, [currentVersion, open]);

  // Initialize emails when data loads
  useEffect(() => {
    if (emailsData?.emails) {
      setEmails(emailsData.emails);
    }
  }, [emailsData]);

  // Validate email format
  const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Add email
  const handleAddEmail = () => {
    const trimmedEmail = emailInput.trim();

    if (!trimmedEmail) {
      setEmailValidation('Email is required');
      return;
    }

    if (!isValidEmail(trimmedEmail)) {
      setEmailValidation('Invalid email format');
      return;
    }

    if (emails.includes(trimmedEmail)) {
      setEmailValidation('Email already added');
      return;
    }

    setEmails([...emails, trimmedEmail]);
    setEmailInput('');
    setEmailValidation('');
  };

  // Remove email
  const handleRemoveEmail = (index: number) => {
    setEmails(emails.filter((_, i) => i !== index));
  };

  // Handle hours change
  const handleHoursChange = (value: string) => {
    setThresholdHours(value);
    // If hours is 12, disable minutes and set to 00
    if (value === '12') {
      setThresholdMinutes('00');
    }
  };

  // Save all changes
  const handleSave = async () => {
    if (!recipe || !currentVersion) {
      setSaveError('Cannot save: recipe version data not loaded yet. Please wait and try again.');
      return;
    }

    setSaveError('');
    setShowSaved(false);

    try {
      const recipeName = recipe.name.endsWith('.lr') ? recipe.name : `${recipe.name}.lr`;

      const updateData = {
        name: recipeName,
        config: currentVersion.config || '',
        tag,
        version: Number(currentVersion.version),
        baseId: currentVersion.baseId,
        id: Number(currentVersion.id),
        description,
        languageVersion: currentVersion.languageVersion || '2.0',
        processingType,
        maxDataProcTime: formatMaxDataProcTime(thresholdHours, thresholdMinutes),
      };

      await updateTransformerInfo({
        appId,
        transId: recipe.baseId,
        data: updateData,
      }).unwrap();

      // Update emails (non-blocking - don't let email save failure prevent close)
      try {
        await updateEmails({
          transBaseId: recipe.baseId,
          emails,
        }).unwrap();
      } catch (emailErr) {
        logger.error('Failed to update notification emails:', emailErr);
      }

      setShowSaved(true);
      setTimeout(() => {
        onClose();
      }, 500);
    } catch (error: unknown) {
      const err = error as { status?: number; data?: unknown; message?: string };
      logger.error('Failed to save recipe properties:', error);
      const errorMsg =
        typeof err?.data === 'string' && err.data
          ? err.data
          : err?.message || 'Failed to save. Please try again.';
      setSaveError(errorMsg);
    }
  };

  if (!recipe) return null;

  const hoursOptions = getHoursOptions();
  const minutesOptions = getMinutesOptions();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <strong>{recipe.name}</strong>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {saveError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSaveError('')}>
            {saveError}
          </Alert>
        )}
        {showSaved && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Saved successfully
          </Alert>
        )}
        <Box sx={{ display: 'flex', gap: 4 }}>
          {/* Left Column: Tag, Processing Type, Description */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
              <TextField
                label="Tag"
                value={tag}
                onChange={(e) => setTag(e.target.value)}
                size="small"
                inputProps={{ maxLength: 15 }}
                sx={{ width: 200 }}
              />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Processing Type
                </Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={processingType === 'stateless'}
                      onChange={(e) => setProcessingType(e.target.checked ? 'stateless' : 'batch')}
                    />
                  }
                  label={processingType === 'stateless' ? 'Stateless' : 'Batch'}
                  labelPlacement="end"
                  sx={{ ml: 0 }}
                />
              </Box>
            </Box>

            <TextField
              label="Description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              fullWidth
              multiline
              rows={10}
              size="small"
            />
          </Box>

          {/* Right Column: Duration Notification, Emails */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {/* Recipe Duration Notification */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Recipe Duration Notification
              </Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <FormControl size="small" sx={{ width: 100 }}>
                  <InputLabel>Hours</InputLabel>
                  <Select
                    value={thresholdHours}
                    label="Hours"
                    onChange={(e) => handleHoursChange(e.target.value)}
                  >
                    {hoursOptions.map((hour) => (
                      <MenuItem key={hour} value={hour}>
                        {hour}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl size="small" sx={{ width: 100 }} disabled={thresholdHours === '12'}>
                  <InputLabel>Minutes</InputLabel>
                  <Select
                    value={thresholdMinutes}
                    label="Minutes"
                    onChange={(e) => setThresholdMinutes(e.target.value)}
                  >
                    {minutesOptions.map((minute) => (
                      <MenuItem key={minute} value={minute}>
                        {minute}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </Box>

            {/* Emails */}
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Emails
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'flex-start' }}>
                <TextField
                  size="small"
                  type="email"
                  value={emailInput}
                  onChange={(e) => {
                    setEmailInput(e.target.value);
                    setEmailValidation('');
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddEmail();
                    }
                  }}
                  placeholder="Enter email"
                  fullWidth
                  error={!!emailValidation}
                  helperText={emailValidation}
                />
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleAddEmail}
                  sx={{ minWidth: 90, whiteSpace: 'nowrap' }}
                >
                  Add Email
                </Button>
              </Box>

              {/* Email List */}
              <Box
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  height: 200,
                  overflowY: 'auto',
                  p: 1,
                }}
              >
                {emails.length === 0 ? (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ textAlign: 'center', py: 2 }}
                  >
                    No notification emails
                  </Typography>
                ) : (
                  emails.map((email, index) => (
                    <Box
                      key={email}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        p: 1,
                        borderBottom: index < emails.length - 1 ? '1px solid' : 'none',
                        borderColor: 'divider',
                      }}
                    >
                      <Button
                        size="small"
                        onClick={() => handleRemoveEmail(index)}
                        sx={{
                          minWidth: 25,
                          width: 25,
                          height: 25,
                          borderRadius: '50%',
                          p: 0,
                        }}
                        variant="outlined"
                        color="error"
                      >
                        ✖
                      </Button>
                      <Typography variant="body2">{email}</Typography>
                    </Box>
                  ))
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button variant="contained" onClick={handleSave} disabled={isSaving}>
          {isSaving ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
